import type { FiscalInvoicePrintData } from '@/types'
import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { FBR_DIGITAL_INVOICING_LOGO_DATA_URI, FBR_QR_PRINT_PIXEL_SIZE } from '@/lib/fbrBranding'

export type FiscalInvoiceDocumentProps = {
  invoice: FiscalInvoicePrintData
  irn: string
  /** data URL or raw base64 PNG */
  qrSrc?: string | null
  /** When set, generates a PNG via `qrcode` (same as thermal receipts). */
  qrPayload?: string | null
  logoUrl?: string
  logoWidthPercent?: number
  orderNumber?: string
  /** Official FBR Digital Invoicing mark (spec layout) */
  showFbrOfficialLogo?: boolean
  /** 'screen' = rounded card; 'print' = flat for print dialog */
  variant?: 'screen' | 'print'
}

/**
 * Shared fiscal invoice layout: DI payload mirror + venue logo + verification QR.
 * Used by Tax & Fiscal test dialog and can be reused for on-screen previews.
 */
export function FiscalInvoiceDocument({
  invoice,
  irn,
  qrSrc,
  qrPayload,
  logoUrl,
  logoWidthPercent = 75,
  orderNumber,
  showFbrOfficialLogo = true,
  variant = 'screen',
}: FiscalInvoiceDocumentProps) {
  const [fromPayload, setFromPayload] = useState<string | null>(null)
  useEffect(() => {
    const p = String(qrPayload ?? '').trim()
    if (!p) {
      setFromPayload(null)
      return
    }
    let cancelled = false
    QRCode.toDataURL(p, { width: FBR_QR_PRINT_PIXEL_SIZE, margin: 2, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (!cancelled) setFromPayload(url)
      })
      .catch(() => {
        if (!cancelled) setFromPayload(null)
      })
    return () => {
      cancelled = true
    }
  }, [qrPayload])

  const wrap =
    variant === 'screen'
      ? 'max-w-md mx-auto rounded-xl border bg-card text-card-foreground shadow-sm overflow-hidden'
      : 'max-w-md mx-auto border border-dashed bg-white text-black'

  let imgSrc =
    qrSrc && !qrSrc.startsWith('data:') && /^[A-Za-z0-9+/]+=*$/.test(qrSrc.replace(/\s/g, ''))
      ? `data:image/png;base64,${qrSrc.replace(/\s/g, '')}`
      : qrSrc || undefined
  if (!imgSrc && fromPayload) imgSrc = fromPayload

  return (
    <div className={wrap}>
      <div className="border-b bg-muted/40 px-4 py-3 text-center">
        {showFbrOfficialLogo ? (
          <img
            src={FBR_DIGITAL_INVOICING_LOGO_DATA_URI}
            alt="FBR Digital Invoicing System"
            className="mx-auto mb-2 h-14 w-auto object-contain"
          />
        ) : null}
        {logoUrl && /^(https?:\/\/|data:image\/)/i.test(logoUrl) ? (
          <img
            src={logoUrl}
            alt=""
            className="mx-auto mb-2 object-contain max-h-12"
            style={{ maxWidth: `${logoWidthPercent}%` }}
          />
        ) : null}
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Digital tax invoice (FBR)
        </h3>
        {orderNumber ? (
          <p className="text-[11px] text-muted-foreground mt-1">Order #{orderNumber}</p>
        ) : null}
      </div>

      <div className="px-4 py-3 space-y-3 text-[11px] leading-snug">
        <section>
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b pb-1 mb-2">
            Seller
          </h4>
          <Field label="Business" value={invoice.seller.name} />
          <Field label="STRN / NTN CNIC" value={invoice.seller.ntn_cnic} mono />
          <Field label="Province" value={invoice.seller.province} />
          {invoice.seller.address ? (
            <p className="text-[11px] whitespace-pre-wrap mt-1">{invoice.seller.address}</p>
          ) : null}
        </section>

        <section>
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b pb-1 mb-2">
            Buyer
          </h4>
          <Field label="Type" value={invoice.buyer.registration_type} />
          <Field label="Name" value={invoice.buyer.name} />
          <Field label="NTN / CNIC" value={invoice.buyer.ntn_cnic} mono />
          <Field label="Province" value={invoice.buyer.province} />
          {invoice.buyer.address ? (
            <p className="text-[11px] whitespace-pre-wrap mt-1">{invoice.buyer.address}</p>
          ) : null}
        </section>

        <section className="grid grid-cols-2 gap-x-2 gap-y-1">
          <Field label="Invoice type" value={invoice.invoice_type} />
          <Field label="Date" value={invoice.invoice_date} />
          <Field label="Ref" value={invoice.invoice_ref_no} mono />
          <Field label="Scenario" value={invoice.scenario_id} />
          {invoice.fbr_posted_at ? <Field label="FBR dated" value={invoice.fbr_posted_at} /> : null}
        </section>

        <div className="overflow-x-auto rounded border">
          <table className="w-full text-[9px]">
            <thead>
              <tr className="bg-muted/60 text-left border-b">
                <th className="p-1 font-semibold">Desc</th>
                <th className="p-1 font-semibold">HS</th>
                <th className="p-1 font-semibold">Qty</th>
                <th className="p-1 font-semibold text-right">Ex-ST</th>
                <th className="p-1 font-semibold">Rate</th>
                <th className="p-1 font-semibold text-right">ST</th>
                <th className="p-1 font-semibold text-right">Tot</th>
                <th className="p-1 font-semibold">FBR #</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((ln) => (
                <tr key={ln.item_s_no} className="border-b border-border/50">
                  <td className="p-1 align-top max-w-[100px]">{ln.product_description}</td>
                  <td className="p-1 align-top font-mono">{ln.hs_code}</td>
                  <td className="p-1 align-top">{ln.quantity}</td>
                  <td className="p-1 align-top text-right tabular-nums">{ln.value_sales_excluding_st.toFixed(2)}</td>
                  <td className="p-1 align-top">{ln.rate}</td>
                  <td className="p-1 align-top text-right tabular-nums">{ln.sales_tax_applicable.toFixed(2)}</td>
                  <td className="p-1 align-top text-right tabular-nums font-medium">{ln.total_values.toFixed(2)}</td>
                  <td className="p-1 align-top font-mono text-[8px] break-all">{ln.fbr_line_invoice_no || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between text-[11px] font-semibold border-t pt-2">
          <span>Grand total</span>
          <span className="tabular-nums">{invoice.totals_grand.toFixed(2)} PKR</span>
        </div>

        <div className="flex flex-col items-center gap-2 pt-2 border-t">
          {imgSrc ? (
            <img
              src={imgSrc}
              width={FBR_QR_PRINT_PIXEL_SIZE}
              height={FBR_QR_PRINT_PIXEL_SIZE}
              className="rounded border bg-white p-1 w-[1in] h-[1in] max-w-full object-contain [image-rendering:pixelated]"
              alt="Verification QR"
            />
          ) : null}
          <div className="text-center w-full">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">FBR invoice / IRN</p>
            <p className="font-mono text-[11px] break-all font-semibold mt-1">{irn}</p>
            <p className="text-[10px] text-muted-foreground mt-2">
              Verify via FBR or authorized tax verification channels.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function Field({
  label,
  value,
  mono,
}: {
  label: string
  value?: string | null
  mono?: boolean
}) {
  if (!value) return null
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className={`text-right ${mono ? 'font-mono text-[10px]' : ''}`}>{value}</span>
    </div>
  )
}
